import { memo, useRef, useEffect, useCallback } from 'react';
import { cn } from '@divlab/divanui';

import isValidHexColor from '@Utils/isValidHexColor';
import hexToHsl from '@Utils/hexToHsl';
import { useDeps } from '@Contexts/DI';
// eslint-disable-next-line @divlab/css-modules/no-unused-class
import styles from './MattressesStack.module.css';

import type { Stack, PathIds, Layer } from './typings';
import type { FC, HTMLAttributes, MouseEvent } from 'react';

export interface MattressesStackProps extends HTMLAttributes<HTMLDivElement> {
  className?: string;
  activeLayers?: number[];
  schemeImage?: string;
  onToggleLayer?: (e: MouseEvent, index: number) => void;
}

const MattressesStack: FC<MattressesStackProps> = (props) => {
  const { className, activeLayers = [], schemeImage, onToggleLayer, ...restProps } = props;
  const { logger } = useDeps();
  const stack = useRef<Stack>(null);
  const refContainer = useRef<HTMLDivElement>();
  const selectedLayers = useRef<number[]>([]);

  const getPathKey = useCallback((path: SVGPathElement): PathIds => {
    const id = path.getAttribute('id') || '';
    const matches = id.match(/^([a-zA-Z-]*).*$/);

    if (!matches) return null;

    return matches[1].replace('-', '_').toLowerCase() as PathIds;
  }, []);

  const setStackHeight = useCallback((height: number) => {
    const w = stack.current.width.toFixed(2);
    const h = height.toFixed(2);

    stack.current.svg.setAttribute('height', `${h}px`);
    stack.current.svg.setAttribute('viewBox', `0 0 ${w} ${h}`);
  }, []);

  const resetSelection = useCallback(() => {
    selectedLayers.current = [];

    stack.current.layers.forEach((layer) => {
      Object.entries(layer.paths).forEach(([key, path]) => {
        path.setAttribute('fill', layer.colors[key as PathIds].main);
      });

      layer.elem.classList.remove(styles.inactived);
      layer.elem.style.transform = 'unset';
    });

    setStackHeight(stack.current.height);
  }, [setStackHeight]);

  const selectLayers = useCallback(
    (indexes: number[]) => {
      selectedLayers.current = indexes;

      let diff = 0;
      stack.current.layers.forEach((layer, index) => {
        const currentIsActived = indexes.includes(index);
        const hasPrevious = index - 1 > -1;
        const previousIsNotActived = !indexes.includes(index - 1);
        const isLast = index === stack.current.layers.length - 1;

        // Обрабатываем неактивные слои
        if (!currentIsActived) {
          Object.entries(layer.paths).forEach(([key, path]) => {
            path.setAttribute('fill', layer.colors[key as PathIds].hover);
          });
          layer.elem.classList.toggle(styles.inactived);
        }

        if (currentIsActived && hasPrevious && previousIsNotActived && !isLast) diff += 20;

        const x = currentIsActived ? '-2%' : 0;
        const transform = [currentIsActived && 'scale(1.04)', `translate3d(${x}, ${diff}px ,0)`]
          .filter(Boolean)
          .join(' ');

        if (currentIsActived) diff += 20;

        layer.elem.style.transform = transform;
      });

      setStackHeight(stack.current.height + diff);
    },
    [setStackHeight],
  );

  const parseSvg = useCallback(
    (svg: SVGSVGElement) => {
      const width = svg.width.baseVal.value;
      const height = svg.height.baseVal.value;
      const groups = Array.from(svg.querySelectorAll('g'));
      const layers = groups.reverse().map((elem) => {
        const colors: Layer['colors'] = {};
        const paths: Layer['paths'] = {};
        const pathElems = Array.from(elem.querySelectorAll('path'));

        pathElems.forEach((pathElem) => {
          const fill = pathElem.getAttribute('fill') || '#fff';
          const key = getPathKey(pathElem);

          if (!key) return;

          paths[key] = pathElem;
          colors[key] = { main: fill, hover: fill };

          if (isValidHexColor(fill)) {
            const { h, s, l } = hexToHsl(fill);
            const color = {
              main: fill,
              hover: `hsl(${h}, ${s}%, ${Math.min(l + 0.75 * (100 - l), 100)}%)`,
            };

            colors[key] = color;

            if (key === 'upper') {
              colors.upper.main = `hsl(${h}, ${s}%, ${Math.max(l - 10, 0)}%)`;
              colors.upper.hover = `hsl(${h}, ${s}%, ${Math.min(l + 0.55 * (100 - l), 100)}%)`;
            }
          }
        });

        return { elem, colors, paths };
      });

      stack.current = { svg, width, height, layers };

      setStackHeight(stack.current.height);
    },
    [getPathKey, setStackHeight],
  );

  const transformSvg = useCallback(
    (svg: SVGSVGElement) => {
      svg.setAttribute('height', `${stack.current.height}px`);
      svg.setAttribute('width', `${stack.current.width}px`);

      const groups = Array.from(svg.querySelectorAll('g'));

      groups.forEach((group, indexGroup) => {
        const paths = Array.from(group.querySelectorAll('path'));

        paths.forEach((path) => {
          const key = getPathKey(path);

          if (!key) return;

          const index = groups.length - indexGroup - 1;
          const layer = stack.current.layers[index];

          path.removeAttribute('id');
          path.setAttribute('fill', layer.colors[key].main);
          path.classList.add(styles[key]);
        });

        group.removeAttribute('id');
      });

      svg.removeAttribute('id');
    },
    [getPathKey],
  );

  const handleClickSvg = useCallback(
    (e: MouseEvent) => {
      let target = e.target as SVGSVGElement;

      try {
        while (target.tagName !== 'svg' && target.tagName !== 'g') {
          target = target.parentElement as unknown as SVGSVGElement;
        }

        const index = stack.current.layers.findIndex((layer) => layer.elem === target);
        const isAlreadyActive = selectedLayers.current.includes(index);

        if (onToggleLayer) onToggleLayer(e, isAlreadyActive ? null : index);
      } catch (err) {
        logger.log(err);
      }
    },
    [onToggleLayer, logger],
  );

  // Запускаем парсинг и трансформацию исходного svg
  useEffect(() => {
    if (!refContainer.current) return;

    const svg = refContainer.current.querySelector('svg');

    parseSvg(svg);
    transformSvg(svg);
  }, [parseSvg, transformSvg]);

  // Выбор/снятие выбора при изменении списка активных слоев
  useEffect(() => {
    if (activeLayers.length > 0) {
      selectLayers(activeLayers);
    } else {
      resetSelection();
    }
  }, [activeLayers, resetSelection, selectLayers]);

  return (
    <div
      {...restProps}
      className={cn(styles.stack, className)}
      // eslint-disable-next-line react/no-danger
      dangerouslySetInnerHTML={{ __html: schemeImage }}
      ref={refContainer}
      onClick={handleClickSvg}
    />
  );
};

export default memo(MattressesStack);
